import { ConfirmationDialog, PoolFormattedMessage } from '@synergy/components';
import { DocumentStatus, LocalizedString, Position, PositionType } from '@synergy/models';
import { FormattedMessage } from 'react-intl';
import { PaloColumn, PaloListView, PaloStatusPill } from '@synergy/components-palo';
import { getLocalizedString, noop } from '@synergy/utils';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import * as PositionsActions from './store/positions-actions';
import { TranslatedField } from '../../components/translated-field';
import { VisibilityIcon } from '../../components/visibility-icon';
import { localesSelector } from '../../../../common/selectors/locale-selectors';
import { positionsSelector } from './store/positions-selector';
import { selectedLanguageSelector } from '../../../../common/selectors/selected-language-selectors';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

export interface PositionsOverviewProps {
    onEdit: (unit: Position) => void;
}

export const PositionsOverview = (props: PositionsOverviewProps) => {
    const dispatch = useThunkDispatch();
    const selectedLanguage = useAppSelector(selectedLanguageSelector);
    const positions = useAppSelector(positionsSelector);
    const availableLocales = useAppSelector(state => state.supportedLocales);
    const currentLocale = useAppSelector(state => state.currentLocale);
    const translationLocales = useAppSelector(localesSelector);
    const [searchParams, setSearchParams] = useSearchParams();

    const updateParams = (changes: Record<string, string>): void => {
        const next = new URLSearchParams(searchParams);
        Object.entries(changes).forEach(([key, value]) => next.set(key, value));
        setSearchParams(next);
    };

    const missingTranslations = (value: LocalizedString): string[] => availableLocales.filter(locale => !value[locale]);

    const columns: PaloColumn<Position>[] = [
        {
            key: 'translation',
            type: 'name',
            head: <FormattedMessage id="app.configuration.positions" />,
            cell: position => (
                <TranslatedField
                    value={position.name}
                    selectedLocale={selectedLanguage}
                    fallback={`ID: ${position.id}`}
                />
            ),
        },
        {
            key: 'showInCustomerPortal',
            type: 'flag',
            head: <FormattedMessage id="app.configuration.positions.showInCustomerPortal" />,
            cell: position => (
                <VisibilityIcon
                    isVisible={PositionType.All === position.type || PositionType.Customer === position.type}
                    isVisibleLabel={<FormattedMessage id="app.configuration.positions.tooltip.showInCustomerPortal" />}
                    isNotVisibleLabel={
                        <FormattedMessage id="app.configuration.positions.tooltip.hideInCustomerPortal" />
                    }
                />
            ),
        },
        {
            key: 'showInSupplierPortal',
            type: 'flag',
            head: <FormattedMessage id="app.configuration.positions.showInSupplierPortal" />,
            cell: position => (
                <VisibilityIcon
                    isVisible={PositionType.All === position.type || PositionType.Supplier === position.type}
                    isVisibleLabel={<FormattedMessage id="app.configuration.positions.tooltip.showInSupplierPortal" />}
                    isNotVisibleLabel={
                        <FormattedMessage id="app.configuration.positions.tooltip.hideInSupplierPortal" />
                    }
                />
            ),
        },
        {
            key: 'isTranslated',
            type: 'flag',
            head: <FormattedMessage id="app.table.column.translation" />,
            cell: position => {
                const missing = missingTranslations(position.name);

                return (
                    <PaloStatusPill
                        status={0 === missing.length ? DocumentStatus.Completed : DocumentStatus.Created}
                        label={
                            0 === missing.length ? (
                                <FormattedMessage id="app.common.translated" />
                            ) : (
                                <FormattedMessage
                                    id="app.common.missingTranslations"
                                    values={{ value: missing.join(', ') }}
                                />
                            )
                        }
                    />
                );
            },
        },
    ];
    const [deleteModalData, setDeleteModalData] = useState<Position | null>(null);

    useEffect(() => {
        dispatch(PositionsActions.fetchPositions(searchParams));
    }, [dispatch, searchParams]);

    const handleDelete = () => {
        if (!deleteModalData?.id || deleteModalData.id < 0) {
            return;
        }
        dispatch(PositionsActions.deletePosition(deleteModalData.id));
        setDeleteModalData(null);
    };

    return (
        <>
            <PaloListView<Position, number>
                embedded={true}
                columns={columns}
                items={positions.items}
                getRowId={position => position.id}
                emptyLabel="Keine Positionen."
                rowActions={position => [
                    { label: <FormattedMessage id="app.common.edit" />, onClick: () => props.onEdit(position) },
                    { label: <FormattedMessage id="app.common.delete" />, onClick: () => setDeleteModalData(position) },
                ]}
                pagination={{
                    count: positions.total,
                    page: Number(searchParams.get('page') ?? 1),
                    limit: Number(searchParams.get('limit') ?? 50),
                    onPageChange: page => updateParams({ page: String(page) }),
                    onLimitChange: limit => updateParams({ limit: String(limit), page: '1' }),
                }}
            />
            <ConfirmationDialog
                isVisible={!!deleteModalData}
                title={<FormattedMessage id="app.configuration.positions.delete" />}
                action={<FormattedMessage id="app.common.delete" />}
                onConfirm={handleDelete}
                onCancel={() => setDeleteModalData(null)}
            >
                <PoolFormattedMessage
                    id="app.configuration.positions.delete.text"
                    values={{ name: getLocalizedString(translationLocales, deleteModalData?.name) }}
                />
            </ConfirmationDialog>
        </>
    );
};
